import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook UI Layer para scroll paginado infinito en listas.
 *
 * Implementa el patrón de **UI Layer** con separación estricta de responsabilidades:
 * - Solo maneja estado visual y eventos de scroll para paginación.
 * - No realiza llamadas a API ni lógica de negocio.
 * - Optimización avanzada: refs, memoización y efectos controlados.
 *
 * @function usePaginatedScrollUI
 * @category Hook - UI Layer
 *
 * @param {Object} params - Parámetros de entrada del hook
 * @param {boolean} params.loading - Estado de carga de la siguiente página
 * @param {Object} params.pagination - Objeto de paginación
 * @param {function} params.onNextPage - Callback para cargar la siguiente página
 * @param {number} [params.threshold=50] - Distancia al borde para disparar
 * @param {'down'|'up'} [params.direction='down'] - Dirección de scroll
 *
 * @returns {Object} Objeto con ref y handler de scroll
 */
export default function usePaginatedScrollUI({
  loading,
  pagination,
  onNextPage,
  threshold = 50,
  direction = 'down'
}) {
  /**
   * Ref de bloqueo para evitar múltiples disparos de paginación.
   */
  const lock = useRef(false);

  /**
   * Ref al contenedor scrollable.
   */
  const scrollContainer = useRef(null);

  /**
   * Ref para guardar el scrollHeight previo (solo para scroll hacia arriba).
   */
  const prevScrollHeight = useRef(null);

  /**
   * Callback ref para asignar el contenedor scrollable.
   */
  const containerRef = useCallback(node => {
    if (node) scrollContainer.current = node;
  }, []);

  /**
   * Handler para el evento onScroll del contenedor.
   */
  const handleScroll = useCallback((e) => {
    if (lock.current || loading) return;

    const el = e.target;
    const { scrollTop, scrollHeight, clientHeight } = el;

    if (direction === 'down') {
      const nearBottom = scrollHeight - scrollTop - clientHeight < threshold;

      if (nearBottom && pagination?.has_next_page) {
        lock.current = true;
        onNextPage?.(pagination.current_page + 1);
      }
    }

    if (direction === 'up') {
      const nearTop = scrollTop < threshold;

      if (nearTop && pagination?.has_next_page) {
        prevScrollHeight.current = scrollHeight;
        lock.current = true;
        onNextPage?.(pagination.current_page + 1);
      }
    }
  }, [loading, pagination, onNextPage, threshold, direction]);

  /**
   * Efecto para desbloquear el lock tras finalizar loading.
   */
  useEffect(() => {
    if (!loading) {
      lock.current = false;
    }
  }, [loading]);

  /**
   * Efecto para ajustar el scroll tras cargar más elementos (solo en scroll hacia arriba).
   */
  useEffect(() => {
    if (
      direction === 'up' &&
      prevScrollHeight.current &&
      scrollContainer.current
    ) {
      const el = scrollContainer.current;

      const newHeight = el.scrollHeight;
      const diff = newHeight - prevScrollHeight.current;

      el.scrollTop = diff;

      prevScrollHeight.current = null;
    }
  }, [direction, pagination?.current_page]);

  return {
    containerRef,
    handleScroll
  };
}
